import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

/**
 * A teammate's GitHub username. Support opens investigation issues through a bot account, so this is what
 * lets the app assign the agent who escalated to the issue.
 */
export interface ISupportGitHubIdentity {
	userId: string;
	gitHubLogin?: string;
}

interface UseGitHubIdentityMutationOptions {
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
}

export function useSupportGitHubIdentitiesQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.gitHubIdentities(mailboxId),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden(
				() =>
					userSession.webServiceHelper.callAsync<ISupportGitHubIdentity[]>(
						SUPPORT_PATHS.mailboxes.gitHubIdentities(mailboxId),
						'GET'
					),
				[] as ISupportGitHubIdentity[]
			),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load GitHub usernames' },
	});
}

/** Sends a blank username to clear the mapping; the server treats that as a removal. */
export function useSetSupportGitHubIdentityMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseGitHubIdentityMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ userId, gitHubLogin }: { userId: string; gitHubLogin: string }) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubIdentity>(
				SUPPORT_PATHS.mailboxes.gitHubIdentity(mailboxId, userId),
				'PUT',
				{ gitHubLogin }
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.gitHubIdentities(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}
